import { useState } from 'react'
import { listCustomers, deleteCustomer, type CustomerRow } from '../api'
import { useTabs, useDataVersion } from '../layout/TabsContext'
import { useToast } from '../layout/ToastContext'
import { useAuth } from '../layout/AuthContext'
import { useServerList } from '../hooks/useServerList'
import { useColumnVisibility } from '../hooks/useColumnVisibility'
import ListToolbar from '../components/ListToolbar'
import ListPanel from '../components/ListPanel'
import Pagination from '../components/Pagination'
import DataTable, { type Column } from '../components/DataTable'
import ColumnManager from '../components/ColumnManager'
import ConfirmDialog from '../components/ConfirmDialog'
import { useBulkTrash } from '../components/BulkTrash'
import { formatMoney } from '../lookups'
import { DEFAULT_TABLE_PAGE_SIZE } from '../../shared/constants'
import { fill, PAGES_CUSTOMERS_LIST_TEXTS as T } from '../../shared/texts'

// Open amounts in the backoffice currency (constants.ts).
const money = (n: number) => formatMoney(n)

// Original icons from references/ (served from public/icons/).
const EditIcon = () => <img src="/icons/edit.svg" alt="" aria-hidden="true" />
const TrashIcon = () => <img src="/icons/delete.svg" alt="" aria-hidden="true" />

export default function CustomersList() {
    const { openTab, publishChange } = useTabs()
    const { toast } = useToast()
    // Refetch when a customer is saved, or when invoices/subscriptions change
    // elsewhere (those drive the OP / #Rechnungen / #Abos columns).
    const version = useDataVersion('customers') + useDataVersion('invoices') + useDataVersion('subscriptions')
    const { preferences, savePreferences, can } = useAuth()
    const canWrite = can('customers', 'write')
    const canDelete = can('customers', 'delete')
    const list = useServerList<CustomerRow>(listCustomers, version, {
        sort: 'createdAt', dir: 'desc', pageSize: preferences.tablePageSize?.customers ?? DEFAULT_TABLE_PAGE_SIZE,
    })
    const cols = useColumnVisibility('customers')

    // Persist the chosen rows-per-page per user.
    const onPageSize = (n: number) => {
        list.setPageSize(n)
        savePreferences({ tablePageSize: { ...(preferences.tablePageSize ?? {}), customers: n } })
    }

    // Delete confirmation: the targeted customer (null = dialog closed) + state.
    const [toDelete, setToDelete] = useState<CustomerRow | null>(null)
    const [delBusy, setDelBusy] = useState(false)
    const [delError, setDelError] = useState('')

    const openNew = () => openTab({ id: 'customer-new', name: T.newTab, component: 'customer-form' })
    const openEdit = (c: CustomerRow) => openTab({
        id: `customer-${c._id}`,
        name: fill(T.editTab, { name: c.company || c.displayName }),
        component: 'customer-form',
        config: { id: c._id },
    })

    const askDelete = (c: CustomerRow) => { setDelError(''); setToDelete(c) }
    const cancelDelete = () => { if (!delBusy) { setToDelete(null); setDelError('') } }

    async function confirmDelete() {
        if (!toDelete) return
        setDelBusy(true); setDelError('')
        try {
            await deleteCustomer(toDelete._id)
            publishChange('customers')
            toast(T.trashed)
            setToDelete(null)
        } catch (e: any) {
            setDelError(e?.response?.data?.error ?? T.deleteFailed)
        } finally {
            setDelBusy(false)
        }
    }

    // Default widths sum below a typical container so the fixed-layout table
    // stretches them to fill 100%; the user can resize from there.
    // Land/Rechn./Abos were nine, eighteen and nine pixels short of their own HEADINGS, so the
    // table read „LANI" / „RECH" / „ABO$". Measured and widened; the spacer column had the
    // room to spare.
    // Mehrfachauswahl → Papierkorb (components/BulkTrash.tsx): same right as the single delete.
    const bulk = useBulkTrash<CustomerRow>({
        kind: 'customers', enabled: canDelete, rows: list.rows, rowId: (r) => r._id, total: list.total,
        filter: { q: list.q },
        onDone: () => publishChange('customers'),
    })

    const columns: Column<CustomerRow>[] = [
        { key: 'company', header: T.colCompany, sortField: 'company', defaultWidth: 140, render: (c) => c.company || '—' },
        { key: 'displayName', header: T.colName, sortField: 'displayName', defaultWidth: 120, render: (c) => c.displayName },
        { key: 'postalCode', header: T.colPostalCode, defaultWidth: 55, render: (c) => c.address?.postalCode || '—' },
        { key: 'city', header: T.colCity, sortField: 'city', defaultWidth: 100, render: (c) => c.address?.city || '—' },
        { key: 'country', header: T.colCountry, sortField: 'country', defaultWidth: 66, render: (c) => c.address?.country || '—' },
        { key: 'email', header: T.colEmail, sortField: 'email', defaultWidth: 160, render: (c) => c.email },
        { key: 'vatId', header: T.colVatId, sortField: 'vatId', defaultWidth: 100, render: (c) => c.vatId || '—' },
        {
            key: 'openCount', header: T.colOpen, sortField: 'openCount', className: 'center', defaultWidth: 50,
            render: (c) => c.openCount > 0
                ? <span className="op-open" title={fill(T.openAmount, { amount: money(c.openGross) })}>{c.openCount}</span>
                : <span className="muted">—</span>,
        },
        { key: 'invoiceCount', header: T.colInvoices, sortField: 'invoiceCount', className: 'center', defaultWidth: 80, render: (c) => c.invoiceCount },
        { key: 'subscriptionCount', header: T.colSubscriptions, sortField: 'subscriptionCount', className: 'center', defaultWidth: 66, render: (c) => c.subscriptionCount },
        {
            key: 'actions', header: T.colActions, defaultWidth: 100, sticky: true,
            render: (c) => (
                <div className="row-actions">
                    <button className="icon-action" title={canWrite ? T.edit : T.view} aria-label={T.edit} onClick={() => openEdit(c)}><EditIcon /></button>
                    {canDelete && <button className="icon-action" title={T.delete} aria-label={T.delete} onClick={() => askDelete(c)}><TrashIcon /></button>}
                </div>
            ),
        },
    ]
    if (bulk.column) columns.unshift(bulk.column)

    return (
        <div className="list-page">
            <ListToolbar
                title={T.title}
                infoText={T.info}
                action={canWrite ? <button onClick={openNew}>{T.newCustomer}</button> : undefined}
            />

            {list.error && <div className="notice err">{list.error}</div>}

            {bulk.bar}

            <ListPanel
                count={list.total}
                grandTotal={list.grandTotal}
                filtered={list.q.trim() !== ''}
                search={{ value: list.q, onChange: list.setQ }}
                columnManager={<ColumnManager columns={columns} hidden={cols.hidden} onToggle={cols.toggle} onReset={cols.reset} />}
            >
                {list.loading ? <p className="muted">{T.loading}</p> : list.rows.length === 0 ? (
                    <p className="muted">{T.empty}</p>
                ) : (
                    <DataTable
                        key={cols.resetKey}
                        tableId="customers"
                        columns={columns}
                        hidden={cols.hidden}
                        rows={list.rows}
                        rowKey={(c) => c._id}
                        sort={list.sort}
                        dir={list.dir}
                        onSort={list.toggleSort}
                        onRowActivate={openEdit}
                    />
                )}
            </ListPanel>

            <Pagination page={list.page} pageCount={list.pageCount} onPage={list.setPage} pageSize={list.pageSize} onPageSize={onPageSize} />

            <ConfirmDialog
                open={!!toDelete}
                title={T.trashTitle}
                message={toDelete
                    ? <>{fill(T.trashQuestion, { name: toDelete.company || toDelete.displayName })}<br />{T.trashAdminsOnly}</>
                    : null}
                confirmLabel={T.trashTitle}
                danger
                busy={delBusy}
                error={delError}
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
            />
            {bulk.dialogs}
        </div>
    )
}
